import { useMemo } from "react";
import type { EChartsOption } from "echarts";
import { Chart } from "./Chart.tsx";
import type { BoxSeries } from "../lib/boxwhisker.ts";

const LINE = "#6699ff";
const BOX = "rgba(102, 153, 255, 0.16)";
const WHISK = "rgba(102, 153, 255, 0.5)";
// forward-filled (a prevailing quote carried into a bucket with no in-bucket
// update): muted, hollow -> reads as "carried", not a fresh observation.
const FILL_FWD = "rgba(150, 165, 190, 0.9)";

function fmtUtc(tsMs: number): string {
  const d = new Date(tsMs);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  const ss = String(d.getUTCSeconds()).padStart(2, "0");
  return ss === "00" ? `${hh}:${mm}` : `${hh}:${mm}:${ss}`;
}

interface Props {
  series: BoxSeries;
  bucketSeconds: number;
  covNoun: string;
  yMin?: number; // 0 for effective/quoted
  zeroLine?: boolean; // realized: include + mark 0
  height?: number;
}

// Box = p25-p75, whiskers = p05-p95 (robust), median tick. The y-axis scales to
// the ENCODED dims (p05..p95) only -- exact min/max ride along in the datum for
// the tooltip but do NOT stretch the axis, so a lone outlier no longer dominates
// the panel. Datum: [tsMs, p05, p25, median, p75, p95, min, max, cov, total].
export function BoxWhiskerChart({
  series,
  bucketSeconds,
  covNoun,
  yMin = 0,
  zeroLine = false,
  height = 200,
}: Props) {
  const option = useMemo<EChartsOption>(() => {
    const bucketMs = bucketSeconds * 1000;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const renderItem = (_params: any, api: any) => {
      const ts = api.value(0);
      const p05 = api.value(1);
      const q1 = api.value(2);
      const med = api.value(3);
      const q3 = api.value(4);
      const p95 = api.value(5);
      const x = api.coord([ts, med])[0];
      const yP05 = api.coord([ts, p05])[1];
      const yQ1 = api.coord([ts, q1])[1];
      const yMed = api.coord([ts, med])[1];
      const yQ3 = api.coord([ts, q3])[1];
      const yP95 = api.coord([ts, p95])[1];
      const wpx = api.size([bucketMs, 0])[0];
      const hw = Math.max(2, Math.min(9, wpx * 0.3));

      const total = api.value(9);
      if (Math.abs(p95 - p05) < 1e-9) {
        // total === 0 -> no in-bucket quote row: this point is a forward-filled
        // prevailing quote. Hollow ring so it reads as carried, distinct from a
        // solid dot (a real single in-bucket observation).
        if (total === 0) {
          return { type: "circle", shape: { cx: x, cy: yMed, r: 3 }, style: { fill: "none", stroke: FILL_FWD, lineWidth: 1.2 } };
        }
        return { type: "circle", shape: { cx: x, cy: yMed, r: 2.5 }, style: { fill: LINE } };
      }
      return {
        type: "group",
        children: [
          { type: "line", shape: { x1: x, y1: yP95, x2: x, y2: yP05 }, style: { stroke: WHISK, lineWidth: 1 } },
          { type: "line", shape: { x1: x - hw * 0.5, y1: yP95, x2: x + hw * 0.5, y2: yP95 }, style: { stroke: WHISK, lineWidth: 1 } },
          { type: "line", shape: { x1: x - hw * 0.5, y1: yP05, x2: x + hw * 0.5, y2: yP05 }, style: { stroke: WHISK, lineWidth: 1 } },
          { type: "rect", shape: { x: x - hw, y: yQ3, width: hw * 2, height: Math.max(1, yQ1 - yQ3) }, style: { fill: BOX, stroke: LINE, lineWidth: 1 } },
          { type: "line", shape: { x1: x - hw, y1: yMed, x2: x + hw, y2: yMed }, style: { stroke: LINE, lineWidth: 2 } },
        ],
      };
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const customSeries: any = {
      type: "custom",
      renderItem,
      // encode only the box+whisker dims so the axis scales to p05..p95, not min/max
      encode: { x: 0, y: [1, 2, 3, 4, 5] },
      data: series.data,
      z: 2,
    };
    if (zeroLine) {
      customSeries.markLine = {
        silent: true,
        symbol: "none",
        lineStyle: { color: "rgba(255,255,255,0.25)", type: "dashed", width: 1 },
        data: [{ yAxis: 0 }],
      };
    }

    // Round the realized floor to a clean step so the axis label isn't a raw
    // float (e.g. -8.354... -> -10), and so all three panels keep identical
    // label widths -> identical left margins -> aligned x-axes.
    const yMinOpt = zeroLine
      ? (v: { min: number; max: number }) => {
          const m = Math.min(0, v.min);
          return m < 0 ? Math.floor(m / 5) * 5 : 0;
        }
      : yMin;

    return {
      grid: { left: 52, right: 16, top: 12, bottom: 28 },
      tooltip: {
        trigger: "item",
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (p: any) => {
          const v = p.value as number[];
          if (!v) return "";
          if (v[9] === 0) {
            // forward-filled: prevailing two-sided quote in effect, no in-bucket
            // update. All five-number dims equal the quote, so show it as one value.
            return (
              `${fmtUtc(v[0])}<br/>prevailing quote ${v[3].toFixed(1)} bps` +
              `<br/>forward-filled (no in-bucket update)`
            );
          }
          return (
            `${fmtUtc(v[0])}<br/>median ${v[3].toFixed(1)} bps` +
            `<br/>p25–p75 ${v[2].toFixed(1)}–${v[4].toFixed(1)} bps` +
            `<br/>whisker p05–p95 ${v[1].toFixed(1)}–${v[5].toFixed(1)} bps` +
            `<br/>range min–max ${v[6].toFixed(1)}–${v[7].toFixed(1)} bps` +
            `<br/>${v[8]} / ${v[9]} ${covNoun}`
          );
        },
      },
      xAxis: { type: "time", min: series.windowStartMs, max: series.windowEndMs },
      yAxis: {
        type: "value",
        name: "bps",
        min: yMinOpt,
        // integer labels keep widths uniform across the three stacked panels
        axisLabel: { formatter: (val: number) => String(Math.round(val)) },
      },
      series: [customSeries],
    };
  }, [series, bucketSeconds, covNoun, yMin, zeroLine]);

  return <Chart option={option} height={height} />;
}
